import clsx from 'clsx';
import { useFormik } from 'formik';
import {
	Bell,
	BookOpen,
	Flame,
	Globe,
	Heart,
	type LucideIcon,
	MessageSquare,
	Newspaper,
	Shield,
	Star,
	Tag,
	Trophy,
	Zap,
} from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { ShowError } from '../../../../errors/components/ShowError';
import { UsersListLoading } from '../../../user-management/users-list/components/loading/UsersListLoading';
import { TagModalState } from '../../tags-table/model/types';
import {
	useCreatePostTag,
	useDeletePostTag,
	useUpdatePostTag,
} from '../api/tags.mutations';

const ICONS: { name: string; component: LucideIcon }[] = [
	{ name: 'tag', component: Tag },
	{ name: 'star', component: Star },
	{ name: 'flame', component: Flame },
	{ name: 'bookopen', component: BookOpen },
	{ name: 'newspaper', component: Newspaper },
	{ name: 'globe', component: Globe },
	{ name: 'shield', component: Shield },
	{ name: 'zap', component: Zap },
	{ name: 'heart', component: Heart },
	{ name: 'trophy', component: Trophy },
	{ name: 'bell', component: Bell },
	{ name: 'messagesquare', component: MessageSquare },
];

const LANGUAGES = ['ru', 'en', 'hi', 'id', 'zh'] as const;
type Lang = typeof LANGUAGES[number];

const EMPTY_LANGS: Record<Lang, string> = {
	ru: '',
	en: '',
	hi: '',
	id: '',
	zh: '',
};

const tagSchema = Yup.object().shape({
	name: Yup.object().shape(
		LANGUAGES.reduce((acc, lang) => {
			acc[lang] = Yup.string()
				.min(3, 'Минимум 3 символа')
				.max(50, 'Максимум 50 символов')
				.required(`Введите название (${lang.toUpperCase()})`);
			return acc;
		}, {} as Record<Lang, Yup.StringSchema>)
	),
	icon: Yup.string()
		.min(3, 'Минимум 3 символа')
		.max(50, 'Максимум 50 символов')
		.required('Введите иконку'),
});

interface Props {
	openModal: TagModalState;
	handleModalClose: () => void;
	setShowErrorToast: (state: boolean) => void;
}

const TagEditModalForm: FC<Props> = ({
	openModal,
	handleModalClose,
	setShowErrorToast,
}) => {
	const [error, setError] = useState();
	const [activeLang, setActiveLang] = useState<Lang>('ru');

	const {
		mutate: createTag,
		isLoading: isTagCreateLoading,
		error: tagCreateError,
	} = useCreatePostTag();

	const {
		mutate: editTag,
		isLoading: isTagEditLoading,
		error: tagEditError,
	} = useUpdatePostTag();

	const {
		mutateAsync: deleteTag,
		isLoading: isTagDeleteLoading,
	} = useDeletePostTag();

	const isLoading =
		isTagCreateLoading || isTagEditLoading || isTagDeleteLoading;

	const isDelete = openModal.type === 'delete' && openModal.data;

	const formik = useFormik({
		initialValues: {
			name: { ...EMPTY_LANGS },
			icon: '',
		},
		validationSchema: !isDelete ? tagSchema : null,
		onSubmit: async (values, { setSubmitting }) => {
			setSubmitting(true);

			try {
				if (openModal.type === 'create') {
					createTag(values);
				} else if (openModal.type === 'edit' && openModal.data) {
					editTag({ uuid: openModal.data.uuid, request: values });
				} else if (openModal.type === 'delete' && openModal.data) {
					await deleteTag(openModal.data.uuid);
				}
			} catch (ex: any) {
				setError(ex);
			} finally {
				setSubmitting(false);
				handleModalClose();
			}
		},
	});

	useEffect(() => {
		if (openModal.type === 'edit' && openModal.data) {
			formik.setValues({
				name: { ...EMPTY_LANGS, ...openModal.data.name },
				icon: openModal.data.icon || '',
			});
			setActiveLang('ru');
		}
	}, [openModal]);

	const nameError = (formik.errors.name as any)?.[activeLang];
	const nameTouched = (formik.touched.name as any)?.[activeLang];

	const langHasError = (lang: Lang) =>
		!!(formik.touched.name as any)?.[lang] &&
		!!(formik.errors.name as any)?.[lang];

	const deleteName = openModal.data?.name
		? (openModal.data.name.ru || Object.values(openModal.data.name)[0] || '')
		: '';

	return (
		<>
			<form
				id='kt_modal_tag_form'
				className='form'
				onSubmit={formik.handleSubmit}
				noValidate
			>
				{/* begin::Scroll */}
				<div
					className='d-flex flex-column scroll-y me-n7 ps-2 pe-7'
					id='kt_modal_add_user_scroll'
					data-kt-scroll='true'
					data-kt-scroll-activate='{default: false, lg: true}'
					data-kt-scroll-max-height='auto'
					data-kt-scroll-dependencies='#kt_modal_add_user_header'
					data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
					data-kt-scroll-offset='300px'
				>
					{openModal.type !== 'delete' && (
						<div>
							{/* begin::Language tabs */}
							<div className='mb-5'>
								<ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0'>
									{LANGUAGES.map(lang => {
										const hasError = langHasError(lang);
										return (
											<li key={lang} className='nav-item'>
												<button
													type='button'
													className={clsx('nav-link text-uppercase fw-bold', {
														active: activeLang === lang,
														'text-danger': hasError,
													})}
													onClick={() => setActiveLang(lang)}
												>
													{lang}
													{hasError && (
														<span className='ms-1 text-danger' aria-hidden='true'>•</span>
													)}
												</button>
											</li>
										);
									})}
								</ul>
							</div>
							{/* end::Language tabs */}

							{/* begin::Input group */}
							<div className='fv-row mb-7'>
								<label className='required fw-bold fs-sm-6 fs-7 mb-2'>
									Название тега
								</label>

								<input
									placeholder='Тег'
									{...formik.getFieldProps(`name.${activeLang}`)}
									type='text'
									className={clsx(
										'form-control form-control-solid mb-3 mb-lg-0',
										{
											'is-invalid': nameTouched && nameError,
										},
										{
											'is-valid': nameTouched && !nameError,
										}
									)}
									autoComplete='off'
									disabled={formik.isSubmitting}
								/>

								{nameTouched && nameError && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>{nameError}</span>
										</div>
									</div>
								)}

								{openModal.type === 'edit' && (
									<div className='form-text'>
										Если у тега есть тема в Telegram-группе новостей, она
										переименуется вслед за названиями RU и EN.
									</div>
								)}
							</div>
							{/* end::Input group */}

							{/* begin::Input group */}
							<div className='fv-row mb-7'>
								<label className='required fw-bold fs-sm-6 fs-7 mb-2'>
									Выберите иконку
								</label>

								<div className='d-flex flex-wrap gap-2'>
									{ICONS.map(({ name, component: Icon }) => (
										<button
											key={name}
											type='button'
											onClick={() => formik.setFieldValue('icon', name)}
											disabled={formik.isSubmitting}
											className={clsx(
												'btn btn-sm d-flex flex-column align-items-center gap-1 px-3 py-2',
												formik.values.icon === name ? 'btn-primary' : 'btn-light'
											)}
											style={{ minWidth: '64px' }}
										>
											<Icon size={20} />
											<span style={{ fontSize: '10px' }}>{name}</span>
										</button>
									))}
								</div>

								{formik.touched.icon && formik.errors.icon && (
									<div className='fv-plugins-message-container mt-2'>
										<div className='fv-help-block'>
											<span role='alert'>{formik.errors.icon}</span>
										</div>
									</div>
								)}
							</div>
							{/* end::Input group */}
						</div>
					)}

					{openModal.type === 'delete' && openModal.data && (
						<div className='fs-6 text-gray-700'>
							Вы собираетесь удалить <strong>{deleteName}</strong>.
							Пожалуйста, подтвердите свое действие.
							<div className='text-muted fs-7 mt-3'>
								Тема этого тега в Telegram-группе новостей будет закрыта, но не
								удалена — опубликованные новости останутся видимыми.
							</div>
						</div>
					)}

					{error && <ShowError error={error} />}
				</div>
				{/* end::Scroll */}

				{/* begin::Actions */}
				<div className='text-center mt-5 '>
					<button
						type='reset'
						className='btn btn-light btn-sm btn-sm-md me-5 min-w-125px'
						onClick={handleModalClose}
						data-kt-users-modal-action='cancel'
						disabled={formik.isSubmitting}
					>
						Отмена
					</button>

					<button
						type='submit'
						className='btn btn-primary  btn-sm btn-sm-md min-w-125px'
						data-kt-users-modal-action='submit'
						disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
					>
						<span className='indicator-label'>
							{openModal.type === 'create'
								? 'Создать'
								: openModal.type === 'edit'
								? 'Изменить'
								: 'Удалить'}
						</span>
						{formik.isSubmitting && (
							<span className='indicator-progress'>
								Пожалуйста подождите...{' '}
								<span className='spinner-border spinner-border-sm align-middle ms-2'></span>
							</span>
						)}
					</button>
				</div>
				{/* end::Actions */}
			</form>
			{formik.isSubmitting && <UsersListLoading />}
		</>
	);
};

export { TagEditModalForm };
